import { Build } from "@stencil/core"

export const getElementsFromNamedSlot = (element: HTMLElement, slotName: string) => {
  const childElements = Array.from(Build.isServer ? element.children : element.querySelectorAll(":scope > *"))
  return childElements.filter(el => el.matches(`[slot='${slotName}']`))
}

export const getElementsFromDefaultSlot = (element: HTMLElement) => {
  const childElements = Array.from(Build.isServer ? element.children : element.querySelectorAll(":scope > *"))
  return childElements.filter(el => !el.getAttribute("slot"))
}

export const hasSlot = (element: HTMLElement, slotName?: string) => {
  return slotName
    ? getElementsFromNamedSlot(element, slotName).length > 0
    : getElementsFromDefaultSlot(element).length > 0
}

export const getSlotTextContent = (element: HTMLElement, slotName?: string, separator?: string) => {
  const elements = slotName ? getElementsFromNamedSlot(element, slotName) : getElementsFromDefaultSlot(element)
  return elements.map(e => e.textContent.trim()).join(separator ?? ", ")
}

/**
 * Check if the slotted element matches the given selector.
 * If not, log a warning, or log a error and remove the element.
 * This function only works with named slots.
 *
 * @param hostElement Host element
 * @param slotName Name of the slot to check.
 * @param selector Css selector the slotted element should match
 * @param maxChildren Maximum number of children allowed in the slot. For no limit use 0. Default is 0.
 * @param remove If true, remove the elements that don't match. Default is false.
 */
export const checkNamedSlotElement = (
  hostElement: HTMLElement,
  slotName: string,
  selector: string,
  maxChildren: number = 0,
  remove: boolean = false
) => {
  const warnOrError = remove ? "error" : "warn"
  const errors = []
  const slottedElements = getElementsFromNamedSlot(hostElement, slotName)
  const nonRemovedElements = slottedElements.filter(el => {
    if (el.matches(selector)) {
      return true
    }
    errors.push(`Only elements matcing selector "${selector}" may be used in slot ${slotName}, found:\n${el.outerHTML}`)
    if (remove) {
      el.remove()
      return false
    }
    return true
  })
  if (maxChildren && nonRemovedElements.length > maxChildren) {
    errors.push(
      `Only ${maxChildren} element${maxChildren > 1 ? "s" : ""} may be used in slot ${slotName}, found ${
        slottedElements.length
      }.`
    )
    if (remove) {
      nonRemovedElements.slice(maxChildren).forEach(el => el.remove())
    }
  }
  if (errors.length > 0) {
    console[warnOrError](
      `Duet ${warnOrError} in ${hostElement.tagName.toLowerCase()}:\n${errors.join("\n")}${
        remove ? `\nElement${errors.length > 1 ? "s" : ""} removed.` : ""
      }`
    )
  }
}

/**
 * Clones host element's default slot contents inside of target element.
 * Uses cloneNode so remember to always check for its caveats.
 *
 * @param hostElement Host element
 * @param targetElement Target element
 */
export const cloneDefaultSlotContent = (hostElement: Element, targetElement: Element) => {
  if (Build.isServer) {
    return
  }
  targetElement.innerHTML = ""
  if (hostElement.childNodes.length === 0) {
    targetElement.innerHTML = hostElement.innerHTML
  } else {
    hostElement.childNodes.forEach(childNode => {
      const copy = childNode.cloneNode(true)
      targetElement.appendChild(copy)
    })
  }
}

/**
 * Creates hidden input element inside component's slot.
 * This can be used to support native <form> element functionalities.
 *
 * @param element Element
 * @param name Input name
 * @param value Input value
 */
export const proxyInputToSlot = (
  element: Element,
  id: string,
  name: string,
  value: any,
  required: boolean,
  checked = undefined
) => {
  if (Build.isServer) {
    return
  }
  const prevInput = element.querySelector<HTMLInputElement>("input")
  const input = prevInput || document.createElement("input")
  if (checked === false) {
    input.remove()
    return
  }

  input.id = id

  if (name !== undefined && name !== null) {
    input.name = name
  }

  if (required) {
    input.type = "text"
    input.hidden = true
    input.required = true
  } else {
    input.type = "hidden"
    input.hidden = false
    input.required = false
  }

  input.value = value

  if (value !== undefined && value !== null) {
    input.value = value
  } else if (value === undefined || value === null) {
    input.value = null
  }

  if (!prevInput) {
    element.appendChild(input)
  }
}
